/**
 * @fileoverview Hook encapsulating the interactive onboarding state machine.
 * Manages the welcome -> form -> chat phase transitions and related data fetching.
 */

import { useCallback, useEffect, useState } from "react";
import { getDefaultTeamUi } from "@/services/team-ui-service";
import { getTeamIntakeFormData, saveTeamIntakeFormData } from "@/services/teams-service";
import type { FormSchema } from "@/types/agent-ui-types";

type InteractivePhase = "welcome" | "form" | "chat";

interface UseInteractiveOnboardingOptions {
  /** Team ID to fetch form schema and intake data for */
  teamId: string;
  /** Whether the chat already has messages (skip to chat phase) */
  hasExistingMessages: boolean;
}

interface UseInteractiveOnboardingResult {
  /** Current phase of the onboarding flow */
  phase: InteractivePhase;
  /** The form schema fetched from the team's default UI */
  formSchema: FormSchema | null;
  /** Display name of the form */
  formName: string;
  /** Existing intake form data (already filled) */
  existingFormData: Record<string, unknown> | null;
  /** Whether data is still loading */
  isLoading: boolean;
  /** Advance from welcome to form (or chat if no form / form already filled) */
  advanceFromWelcome: () => void;
  /** Submit the form, persist data, and return the initial message for chat */
  submitForm: (values: Record<string, unknown>) => Promise<string>;
}

/**
 * Encapsulates the interactive onboarding state machine and data fetching.
 * Handles the welcome -> form -> chat flow outside of the Chat component.
 */
export function useInteractiveOnboarding({
  teamId,
  hasExistingMessages,
}: UseInteractiveOnboardingOptions): UseInteractiveOnboardingResult {
  const [phase, setPhase] = useState<InteractivePhase>(
    hasExistingMessages ? "chat" : "welcome"
  );
  const [formSchema, setFormSchema] = useState<FormSchema | null>(null);
  const [formName, setFormName] = useState("");
  const [existingFormData, setExistingFormData] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(!hasExistingMessages);

  /** Fetch form schema and existing intake data */
  useEffect(() => {
    if (hasExistingMessages) return;

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [formResult, intakeResult] = await Promise.allSettled([
          getDefaultTeamUi(teamId),
          getTeamIntakeFormData(teamId),
        ]);

        if (cancelled) return;

        if (formResult.status === "fulfilled" && formResult.value?.formSchema) {
          setFormSchema(formResult.value.formSchema as FormSchema);
          setFormName(formResult.value.name || "Intake Form");
        }

        if (intakeResult.status === "fulfilled" && intakeResult.value) {
          setExistingFormData(intakeResult.value);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [teamId, hasExistingMessages]);

  /** Advance from welcome to form, or directly to chat if no form or form already filled */
  const advanceFromWelcome = useCallback(() => {
    if (!formSchema || existingFormData) {
      setPhase("chat");
    } else {
      setPhase("form");
    }
  }, [formSchema, existingFormData]);

  /** Submit the form, persist data, and transition to chat */
  const submitForm = useCallback(async (values: Record<string, unknown>): Promise<string> => {
    setPhase("chat");

    // Persist form data (non-blocking for UX)
    saveTeamIntakeFormData(teamId, values)
      .then(() => setExistingFormData(values))
      .catch(() => {
        // Non-blocking — form data will just not be persisted
      });

    return "I've completed the intake form. Please begin my assessment.";
  }, [teamId]);

  return {
    phase,
    formSchema,
    formName,
    existingFormData,
    isLoading,
    advanceFromWelcome,
    submitForm,
  };
}
